import { useAppContext } from '../../../context/AppContext';
import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faUpload, faFilePdf, faFileImage, faFileExcel, faFileWord, faFileCsv, faFile, faXmark, faCamera } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../helpers/Modal';
// Allowed file types configuration
const CAD_DXF_COLOR = '#0ea5e9';
const CAD_STEP_COLOR = '#6366f1';

const ALLOWED_FILE_TYPES = {
    'application/pdf': { ext: 'pdf', icon: faFilePdf, color: '#dc2626' },
    'image/png': { ext: 'png', icon: faFileImage, color: '#638a47' },
    'image/jpeg': { ext: 'jpg', icon: faFileImage, color: '#638a47' },
    'image/heic': { ext: 'heic', icon: faFileImage, color: '#638a47' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { ext: 'xlsx', icon: faFileExcel, color: '#16a34a' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: 'docx', icon: faFileWord, color: '#2563eb' },
    'text/csv': { ext: 'csv', icon: faFileCsv, color: '#ca8a04' },
    // DXF — browsers often use empty/octet-stream; extension check still allows these
    'image/vnd.dxf': { ext: 'dxf', icon: faFile, color: CAD_DXF_COLOR },
    'application/dxf': { ext: 'dxf', icon: faFile, color: CAD_DXF_COLOR },
    'application/x-dxf': { ext: 'dxf', icon: faFile, color: CAD_DXF_COLOR },
    // STEP — .step and .stp
    'model/step': { ext: 'step', icon: faFile, color: CAD_STEP_COLOR },
    'application/step': { ext: 'step', icon: faFile, color: CAD_STEP_COLOR },
    'application/x-step': { ext: 'step', icon: faFile, color: CAD_STEP_COLOR },
    'model/stp': { ext: 'stp', icon: faFile, color: CAD_STEP_COLOR },
    'application/stp': { ext: 'stp', icon: faFile, color: CAD_STEP_COLOR },
};

const ALLOWED_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'heic', 'xlsx', 'docx', 'csv', 'dxf', 'step', 'stp'];

const getFileTypeInfo = (file) => {
    if (ALLOWED_FILE_TYPES[file.type]) {
        return ALLOWED_FILE_TYPES[file.type];
    }
    const extension = file.name.split('.').pop()?.toLowerCase();
    for (const info of Object.values(ALLOWED_FILE_TYPES)) {
        if (info.ext === extension || (extension === 'jpeg' && info.ext === 'jpg')) {
            return info;
        }
    }
    return null;
};

const isFileAllowed = (file) => {
    if (ALLOWED_FILE_TYPES[file.type]) {
        return true;
    }
    const extension = file.name.split('.').pop()?.toLowerCase();
    return ALLOWED_EXTENSIONS.includes(extension);
};


export const NewFileForm = ({
    project,
    onUploadSuccess,
    showTriggerButtons = true,
    controlledModalOpen,
    onControlledModalClose,
    uploadPreset = null,
}) => {
    const { token, base_url } = useAppContext();
    const [internalModalOpen, setInternalModalOpen] = useState(false);
    const isControlled = typeof controlledModalOpen === 'boolean';
    const isModalVisible = isControlled ? controlledModalOpen : internalModalOpen;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [isTraveler, setIsTraveler] = useState(false);
    const [isPackagingPhoto, setIsPackagingPhoto] = useState(false);
    const [restrictToImages, setRestrictToImages] = useState(false);
    const [associationType, setAssociationType] = useState(null);
    const [associatedId, setAssociatedId] = useState(null);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            setIsMobile(hasTouchScreen && isMobileUserAgent);
        };
        checkMobile();
    }, []);

    useEffect(() => {
        if (!isModalVisible || !uploadPreset) return;
        setIsTraveler(Boolean(uploadPreset.is_traveler));
        setIsPackagingPhoto(
            Boolean(uploadPreset.is_packaging_photo ?? uploadPreset.is_packaging_photos)
        );
        setRestrictToImages(Boolean(uploadPreset.restrict_to_images));
        setAssociationType(uploadPreset.association_type ?? null);
        setAssociatedId(
            uploadPreset.associated_id ?? uploadPreset.association_id ?? null
        );
    }, [isModalVisible, uploadPreset]);

    const isImageFile = (file) => {
        if (file.type && file.type.startsWith('image/')) return true;
        const ext = file.name.split('.').pop()?.toLowerCase();
        return ext === 'heic' || ext === 'jpg' || ext === 'jpeg' || ext === 'png';
    };

    const validateAndAddFiles = (incomingFiles) => {
        const toAdd = [];
        for (const file of incomingFiles) {
            if (restrictToImages) {
                if (!isImageFile(file)) {
                    window.showAlert(400, `"${file.name}" must be an image (PNG, JPG, JPEG, or HEIC)`);
                    continue;
                }
            } else if (!isFileAllowed(file)) {
                window.showAlert(400, `"${file.name}" is not a valid file type (PDF, PNG, JPG, JPEG, HEIC, XLSX, DOCX, CSV, DXF, STEP, or STP)`);
                continue;
            }
            const maxSize = 25 * 1024 * 1024;
            if (file.size > maxSize) {
                window.showAlert(400, `"${file.name}" exceeds the 25MB size limit`);
                continue;
            }
            toAdd.push(file);
        }
        if (toAdd.length > 0) {
            setSelectedFiles(prev => [...prev, ...toAdd]);
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files?.length) {
            validateAndAddFiles(Array.from(e.target.files));
        }
    };

    const handleCameraCapture = (e) => {
        if (e.target.files?.length) {
            validateAndAddFiles(Array.from(e.target.files));
            if (!isControlled) setInternalModalOpen(true);
        }
        e.target.value = '';
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files?.length) {
            validateAndAddFiles(Array.from(e.dataTransfer.files));
        }
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const clearFiles = () => {
        setSelectedFiles([]);
        setIsTraveler(false);
        setIsPackagingPhoto(false);
        setRestrictToImages(false);
        setAssociationType(null);
        setAssociatedId(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const getExtensionForRename = (file) => {
        const fromName = file.name.includes('.') ? file.name.split('.').pop() : '';
        const ext = (fromName || '').toLowerCase();
        if (ext === 'jpeg') return 'jpg';
        if (['png', 'jpg', 'heic'].includes(ext)) return ext;
        if (file.type === 'image/png') return 'png';
        if (file.type === 'image/heic') return 'heic';
        if (file.type === 'image/jpeg') return 'jpg';
        return ext || 'jpg';
    };

    const buildRenamedDesignPhotoFile = (file, fileIndex) => {
        const preset = uploadPreset;
        if (!preset?.rename_uploads_with_design_suffix || preset.design_unique_id == null) {
            return file;
        }
        const uid = String(preset.design_unique_id);
        const suffix = uid.length >= 4 ? uid.slice(-4) : uid;
        const ext = getExtensionForRename(file);
        const newName = `${suffix}_${fileIndex + 1}.${ext}`;
        return new File([file], newName, { type: file.type });
    };

    const uploadSingleFile = async (file, fileIndex) => {
        const fileToUpload = buildRenamedDesignPhotoFile(file, fileIndex);

        const metadata = {
            project_site_id: project.id,
            is_traveler: isTraveler,
            is_packaging_photo: isPackagingPhoto,
            is_packaging_photos: isPackagingPhoto,
        };
        if (associationType != null && associatedId != null) {
            metadata.association_type = associationType;
            metadata.associated_id = associatedId;
        }
        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('metadata', JSON.stringify(metadata));

        const response = await fetch(`${base_url}/apis/uploads/create`, {
            method: 'POST',
            headers: { 'Authorization': token },
            body: formData,
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(result.msg || `Failed to upload "${fileToUpload.name}"`);
        }
        return result;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        if (selectedFiles.length === 0) {
            window.showAlert(400, 'Please select at least one file to upload');
            return;
        }

        setIsSubmitting(true);

        try {
            const results = await Promise.all(selectedFiles.map((f, i) => uploadSingleFile(f, i)));

            const count = results.length;
            window.showAlert(200, count === 1 ? 'File uploaded successfully!' : `${count} files uploaded successfully!`);

            clearFiles();
            if (isControlled) {
                onControlledModalClose?.();
            } else {
                setInternalModalOpen(false);
            }

            if (onUploadSuccess) {
                onUploadSuccess(results);
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            window.showAlert(500, error.message || 'Failed to upload files. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeModal = () => {
        clearFiles();
        setIsSubmitting(false);
        if (isControlled) {
            onControlledModalClose?.();
        } else {
            setInternalModalOpen(false);
        }
    };

    const fileInputAccept = restrictToImages
        ? 'image/png,image/jpeg,image/heic,.png,.jpg,.jpeg,.heic'
        : '.pdf,.png,.jpg,.jpeg,.heic,.xlsx,.docx,.csv,.dxf,.step,.stp,application/pdf,image/png,image/jpeg,image/heic,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,image/vnd.dxf,application/dxf,application/x-dxf,model/step,application/step,application/x-step,model/stp,application/stp';

    const uploadForm = (
        <div>
            <h2 className="modal-title">
                {restrictToImages
                    ? `Upload Photo${selectedFiles.length > 1 ? 's' : ''}`
                    : `Upload File${selectedFiles.length > 1 ? 's' : ''}`}
            </h2>
            <form onSubmit={handleSubmit}>
                {restrictToImages && isMobile && (
                    <div className="modal-camera-row">
                        <input
                            type="file"
                            ref={cameraInputRef}
                            onChange={handleCameraCapture}
                            accept="image/*"
                            capture="environment"
                            className="file-input-hidden"
                        />
                        <button
                            type="button"
                            className="btn btn-action btn-secondary modal-take-photo-btn"
                            onClick={() => cameraInputRef.current?.click()}
                        >
                            <FontAwesomeIcon icon={faCamera} /> Take Photo
                        </button>
                    </div>
                )}
                <div className="form-group">
                    <label className="form-label">{restrictToImages ? 'Select Photos *' : 'Select Files *'}</label>
                    <div
                        className={`file-drop-zone ${dragActive ? 'drag-active' : ''} ${selectedFiles.length > 0 ? 'has-file' : ''}`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            accept={fileInputAccept}
                            multiple
                            className="file-input-hidden"
                        />

                        <div className="drop-zone-content">
                            <FontAwesomeIcon icon={faUpload} className="upload-icon" />
                            <p className="drop-zone-text">Drag & drop {restrictToImages ? 'images' : 'files'} here</p>
                            <p className="drop-zone-subtext">or click to browse</p>
                            <span className="file-types">{restrictToImages ? 'PNG, JPG, JPEG, HEIC' : 'PDF, PNG, JPG, HEIC, XLSX, DOCX, CSV, DXF, STEP, STP'}</span>
                            <span className="file-limit">Max 25MB per file</span>
                        </div>
                    </div>

                    {selectedFiles.length > 0 && (
                        <div className="file-list">
                            {selectedFiles.map((file, index) => {
                                const fileInfo = getFileTypeInfo(file);
                                return (
                                    <div key={index} className="file-list-item">
                                        <FontAwesomeIcon
                                            icon={fileInfo?.icon || faFile}
                                            className="file-icon"
                                            style={{ color: fileInfo?.color || 'var(--gray)' }}
                                        />
                                        <div className="file-details">
                                            <span className="file-name">{file.name}</span>
                                            <span className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="clear-file-btn"
                                            onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                                        >
                                            <FontAwesomeIcon icon={faXmark} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {!restrictToImages && (
                    <div className="checkbox-group">
                        <label className={`checkbox-label ${isPackagingPhoto ? 'checkbox-disabled' : ''}`}>
                            <input
                                type="checkbox"
                                checked={isTraveler}
                                disabled={isPackagingPhoto}
                                onChange={(e) => setIsTraveler(e.target.checked)}
                            />
                            <span>Traveler</span>
                        </label>
                        <label className={`checkbox-label ${isTraveler ? 'checkbox-disabled' : ''}`}>
                            <input
                                type="checkbox"
                                checked={isPackagingPhoto}
                                disabled={isTraveler}
                                onChange={(e) => setIsPackagingPhoto(e.target.checked)}
                            />
                            <span>Packaging Photo</span>
                        </label>
                    </div>
                )}

                <div className="form-actions">
                    <button type="button" onClick={closeModal} className="btn btn-secondary">
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting || selectedFiles.length === 0}
                    >
                        {isSubmitting ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /> Uploading...</>
                        ) : (
                            <><FontAwesomeIcon icon={faUpload} /> Upload {selectedFiles.length > 1 ? `${selectedFiles.length} ${restrictToImages ? 'Photos' : 'Files'}` : restrictToImages ? 'Photo' : 'File'}</>
                        )}
                    </button>
                </div>
            </form>

            <style>{`
                .modal-camera-row {
                    margin-bottom: 1rem;
                }

                .modal-take-photo-btn {
                    width: 100%;
                    justify-content: center;
                }

                .file-drop-zone {
                    border: 2px dashed var(--gray);
                    border-radius: 8px;
                    padding: 2rem;
                    text-align: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    background-color: rgba(0, 0, 0, 0.02);
                }

                .file-drop-zone:hover {
                    border-color: var(--dark);
                    background-color: rgba(0, 0, 0, 0.04);
                }

                .file-drop-zone.drag-active {
                    border-color: var(--primary);
                    background-color: rgba(59, 130, 246, 0.1);
                }

                .file-drop-zone.has-file {
                    border-color: var(--primary);
                    background-color: rgba(59, 130, 246, 0.03);
                }

                .drop-zone-content {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                }

                .upload-icon {
                    font-size: 2.5rem;
                    color: var(--gray);
                    margin-bottom: 0.5rem;
                }

                .drop-zone-text {
                    font-size: 1rem;
                    font-weight: 500;
                    color: var(--dark);
                    margin: 0;
                }

                .drop-zone-subtext {
                    font-size: 0.875rem;
                    color: var(--gray);
                    margin: 0;
                }

                .file-limit {
                    font-size: 0.75rem;
                    color: var(--gray);
                    margin-top: 0.5rem;
                }

                .file-types {
                    font-size: 0.75rem;
                    color: var(--gray);
                    margin-top: 0.5rem;
                    font-weight: 500;
                }

                .file-list {
                    margin-top: 0.75rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    max-height: 220px;
                    overflow-y: auto;
                }

                .file-list-item {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.5rem 0.75rem;
                    border: 1px solid rgba(0,0,0,0.08);
                    border-radius: 6px;
                    background-color: rgba(0,0,0,0.02);
                }

                .file-icon {
                    font-size: 1.5rem;
                    flex-shrink: 0;
                }

                .file-details {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    flex: 1;
                    min-width: 0;
                }

                .file-name {
                    font-weight: 500;
                    color: var(--dark);
                    word-break: break-all;
                    font-size: 0.875rem;
                }

                .file-size {
                    font-size: 0.75rem;
                    color: var(--gray);
                }

                .clear-file-btn {
                    background: none;
                    border: none;
                    color: var(--gray);
                    cursor: pointer;
                    padding: 0.4rem;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .clear-file-btn:hover {
                    background-color: rgba(0, 0, 0, 0.1);
                    color: var(--dark);
                }

                .checkbox-group {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    margin-top: 1rem;
                }

                .checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                    font-size: 0.9rem;
                    color: var(--dark);
                    user-select: none;
                }

                .checkbox-label input[type="checkbox"] {
                    width: 16px;
                    height: 16px;
                    cursor: pointer;
                    accent-color: var(--primary);
                    flex-shrink: 0;
                }

                .checkbox-disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                .checkbox-disabled input[type="checkbox"] {
                    cursor: not-allowed;
                }

                @media (max-width: 480px) {
                    .file-drop-zone {
                        padding: 1.5rem 1rem;
                    }

                    .upload-icon {
                        font-size: 2rem;
                    }

                    .drop-zone-text {
                        font-size: 0.9rem;
                    }
                }
            `}</style>
        </div>
    );

    return (
        <div>
            {showTriggerButtons && (
                <>
                    <div className="upload-buttons">
                        <button
                            type="button"
                            className="btn btn-action btn-primary"
                            onClick={() => setInternalModalOpen(true)}
                        >
                            <FontAwesomeIcon icon={faUpload} /> Upload File
                        </button>
                        {isMobile && (
                            <>
                                <input
                                    type="file"
                                    ref={cameraInputRef}
                                    onChange={handleCameraCapture}
                                    accept="image/*"
                                    capture="environment"
                                    className="file-input-hidden"
                                />
                                <button
                                    type="button"
                                    className="btn btn-action btn-secondary"
                                    onClick={() => cameraInputRef.current?.click()}
                                >
                                    <FontAwesomeIcon icon={faCamera} /> Take Photo
                                </button>
                            </>
                        )}
                    </div>
                    <style>{`
                        .upload-buttons {
                            display: flex;
                            gap: 0.5rem;
                            flex-wrap: wrap;
                        }
                    `}</style>
                </>
            )}
            {isModalVisible && (
                <Modal form={uploadForm} setIsModalOpen={closeModal} />
            )}
        </div>
    );
};
